import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Avatar,
  Tooltip,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { fetchUserNameById } from '../DatabaseApis/DBQuery';

const CRMCardDetailsDialog = ({ open, onClose, card, onEdit }) => {
  // Simple check: if card status is "Won" or "Lost", disable editing
  const isFinalized = card?.cardStatus === 'Won' || card?.cardStatus === 'Lost';
  
  // State variables to hold the usernames of the user who created and updated the card
  const [createdByUserName, setCreatedByUserName] = useState(null);
  const [updatedByUserName, setUpdatedByUserName] = useState(null);

  useEffect(() => {
    const fetchUserNames = async () => {
      if (card) {
        const createdBy = await fetchUserNameById(card.cardCreatedByUserId);
        const updatedBy = await fetchUserNameById(card.cardUpdatedByUserId);
        
        setCreatedByUserName(createdBy);
        setUpdatedByUserName(updatedBy);
      }
    };
  
    fetchUserNames();
  }, [card]);

  if (!card) return null;
  const template = card.templateData || {};
  const assignee = card.assigneeName || template.assigned_to || 'N/A';
  const ragStatus = template.rag || 'Unassigned';
  const priority = card.priority || template.priority;
  const dealValueCurrency = template.deal_value_currency || 'USD';
  
  // Currency symbol mapping
  const currencySymbols = {
    'INR': '₹',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'SGD': 'S$',
    'JPY': '¥'
  };
  const currencySymbol = currencySymbols[dealValueCurrency] || '$';

  /**
   * Formats timestamp to a consistent format: DD/MMM/YYYY, HH:MM:SS AM/PM
   */
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown Date';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Unknown Date';
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    // Split the date and time parts
    const [datePart, timePart] = formattedDate.split(', ');
    return `${datePart.replace(/ /g, "/")}, ${timePart || ''}`;
  };

  const localCreationDate = card.cardCreationDateDisplay
    ? formatTimestamp(card.cardCreationDateDisplay)
    : 'Unknown Date';

  const localUpdatedDate = card.cardUpdatedOn
    ? formatTimestamp(card.cardUpdatedOn)
    : 'Unknown Date';

  // Get RAG status color
  const getRAGStatusColor = (ragStatus) => {
    switch (ragStatus) {
      case 'Red':
        return '#EF4444';
      case 'Amber':
        return '#F59E0B';
      case 'Green':
        return '#10B981';
      case 'Unassigned':
        return '#9E9E9E'; // Gray color for Unassigned
      default:
        return '#9E9E9E'; // Default to gray for Unassigned
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return '#f57c00';
      case 'Medium':
        return '#fbc02d';
      case 'Low':
        return '#388e3c';
      default:
        return '#757575';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        // Only allow closing via explicit close button, not backdrop click or escape key
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
    >
      <Box sx={{ background: 'linear-gradient(45deg, #1e88e5 30%, #66bb6a 90%)', color: '#fff', fontWeight: 'bold', textAlign: 'center', position: 'relative' }}>
        <DialogTitle sx={{ color: 'white' }}>
          CRM Card Details
          
          {/* Edit Button - hidden if card is finalized */}
          {onEdit && !isFinalized && (
            <IconButton
              onClick={() => onEdit(card)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8, 
                color: 'white',
              }}
            >
              <EditIcon />
            </IconButton>
          )}

          {/* Close Button */}
          <IconButton onClick={onClose} sx={{ position: 'absolute', left: 8, top: 8, color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      </Box>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12}>
            <Typography variant="h6">{card.summary || template.name || 'N/A'}</Typography>
          </Grid>

          {/* CRM ID */}
          <Grid item xs={12}>
            <Typography variant="body1"><strong>CRM ID:</strong> {card.cardId}</Typography>
          </Grid>

          {/* Partition Divider
          <Grid item xs={12}>
            <Divider sx={{
              my: 2,
              borderBottomWidth: 2,
              borderColor: "#4e54c8",
              borderRadius: 2,
              borderStyle: "dashed",
            }} />
            <Typography variant="subtitle2" sx={{ color: '#4e54c8', fontWeight: 'bold', mb: 2 }}>
              Common Fields
            </Typography>
          </Grid> */}

          {/* Common Fields - Above Partition */}
          {/* Email */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body1"><strong>Email:</strong> {template.email || 'N/A'}</Typography>
          </Grid>

          {/* Phone */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body1"><strong>Phone:</strong> {template.phone || 'N/A'}</Typography>
          </Grid>

          {/* Company */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body1"><strong>Company:</strong> {template.company || 'N/A'}</Typography>
          </Grid>

          {/* Relation Entity */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body1"><strong>Relation Entity:</strong> {template.relation_entity || 'N/A'}</Typography>
          </Grid>

          {/* Partition Divider */}
          <Grid item xs={12}>
            <Divider sx={{
              my: 2,
              borderBottomWidth: 2,
              borderColor: "#4e54c8",
              borderRadius: 2,
              borderStyle: "dashed",
            }} />
            <Typography variant="subtitle2" sx={{ color: '#4e54c8', fontWeight: 'bold', mb: 2 }}>
              Deal Information
            </Typography>
          </Grid>

          {/* Fields Below Partition */}
          {/* Deal Value */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body1"><strong>Deal Value:</strong> {template.deal_value ? `${currencySymbol}${template.deal_value.toLocaleString('en-IN')}` : 'N/A'}</Typography>
          </Grid>

          {/* Stage */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body1"><strong>Stage:</strong> {card.cardStatus || 'N/A'}</Typography>
          </Grid>

          {/* Priority and RAG Status */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1"><strong>Priority:</strong></Typography>
              <Chip
                label={priority || 'N/A'}
                size="small"
                sx={{
                  backgroundColor: getPriorityColor(priority),
                  color: "#fff",
                  fontSize: "0.8rem",
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1"><strong>RAG Status:</strong></Typography>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: getRAGStatusColor(ragStatus),
                  border: '1px solid #fff',
                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                }}
                title={`RAG Status: ${ragStatus}`}
              />
              <Typography variant="body2" sx={{ ml: 1 }}>{ragStatus}</Typography>
            </Box>
          </Grid>

          {/* Assigned To */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIndIcon sx={{ color: '#1976d2' }} />
              <Typography variant="body1"><strong>Assigned To:</strong> {assignee}</Typography>
            </Box>
          </Grid>

          {/* Source */}
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Source:</strong> {template.source || 'N/A'}</Typography>
          </Grid>

          {/* Tags */}
          <Grid item xs={12}>
            <Typography variant="body1"><strong>Tags:</strong></Typography>
            {template.tags && template.tags.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {template.tags.map((tag, index) => (
                  <Chip key={index} label={tag} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No tags</Typography>
            )}
          </Grid>

          {/* RAG Status History - Disabled for CRM Template */}
          {/* {template.ragStatusHistory && template.ragStatusHistory.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2 }}>RAG Status History</Typography>
              <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {template.ragStatusHistory.map((change, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: getRAGStatusColor(change.previousStatus),
                          border: '1px solid #fff',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                        }}
                        title={`Previous: ${change.previousStatus}`}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {change.previousStatus}
                      </Typography>
                      <Typography variant="body2">→</Typography>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: getRAGStatusColor(change.currentStatus),
                          border: '1px solid #fff',
                          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
                        }}
                        title={`Current: ${change.currentStatus}`}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {change.currentStatus}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Changed by {change.user} on {change.dateTime ? new Date(change.dateTime).toLocaleString() : 'Unknown Date'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          )} */}

          {/* Attachments */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1"><strong>Attachments:</strong></Typography>
            {template.attachments && template.attachments.length > 0 ? (
              <Box sx={{ pl: 2, mt: 1 }}>
                {template.attachments.map((file, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderBottom: '1px solid #eee' }}>
                    <Typography component="a" href={file.url} target="_blank" rel="noopener noreferrer" sx={{ color: '#4e54c8', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{file.name}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 2, mt: 1 }}>No attachments</Typography>
            )}
          </Grid>

          {/* Comments */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body1"><strong>Comments:</strong></Typography>
            {template.comments && template.comments.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                {template.comments.filter(comment => !comment.isDeleted).map((comment) => (
                  <Box key={comment.id} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: '#4e54c8' }}>{comment.authorEmail ? comment.authorEmail.charAt(0).toUpperCase() : '?'}</Avatar>
                    <Box>
                      <Typography variant="body2"><strong>{comment.authorEmail || 'Anonymous'}</strong></Typography>
                      <Typography variant="caption" color="text.secondary">{new Date(comment.timestamp).toLocaleString()}</Typography>
                      <Typography variant="body2">{comment.text}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No comments</Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default CRMCardDetailsDialog;

